'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type ProjectStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

interface BOMLine {
  id: string;
  itemCode: string;
  itemName: string;
  unit: string;
  plannedQty: number;
  issuedQty: number;
  returnedQty: number;
}

interface BOMProject {
  id: string;
  projectName: string;
  projectCode: string;
  site: string;
  projectManager: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  totalBOMValue: number;
  lines: BOMLine[];
}

const PROJECTS: BOMProject[] = [
  {
    id: 'BOM-001',
    projectName: 'Refinery Expansion — Phase 2',
    projectCode: 'REF-EXP-P2',
    site: 'Site Alpha',
    projectManager: 'Arjun Mehta',
    startDate: '01 Mar 2026',
    endDate: '30 Sep 2026',
    status: 'ACTIVE',
    totalBOMValue: 284500,
    lines: [
      { id: 'L01', itemCode: 'STL-PIPE-DN150', itemName: 'Steel Pipe DN150 (6m)', unit: 'PCS', plannedQty: 120, issuedQty: 108, returnedQty: 4 },
      { id: 'L02', itemCode: 'WELD-ROD-E7018', itemName: 'Welding Rod E7018 (5kg)', unit: 'PKT', plannedQty: 200, issuedQty: 195, returnedQty: 0 },
      { id: 'L03', itemCode: 'BOLT-M20-SS', itemName: 'Bolt M20 Stainless (box)', unit: 'BOX', plannedQty: 50, issuedQty: 38, returnedQty: 2 },
      { id: 'L04', itemCode: 'GASKET-SPIRAL-6', itemName: 'Spiral Wound Gasket 6"', unit: 'PCS', plannedQty: 80, issuedQty: 87, returnedQty: 0 },
      { id: 'L05', itemCode: 'INSUL-ROCK-50MM', itemName: 'Rockwool Insulation 50mm', unit: 'M2', plannedQty: 600, issuedQty: 320, returnedQty: 10 },
    ],
  },
  {
    id: 'BOM-002',
    projectName: 'Warehouse Racking Installation',
    projectCode: 'WH-RACK-2026',
    site: 'Warehouse B',
    projectManager: 'Priya Sharma',
    startDate: '15 Apr 2026',
    endDate: '15 Jul 2026',
    status: 'COMPLETED',
    totalBOMValue: 62300,
    lines: [
      { id: 'L01', itemCode: 'RACK-UPRIGHT-4M', itemName: 'Racking Upright 4m', unit: 'PCS', plannedQty: 60, issuedQty: 60, returnedQty: 0 },
      { id: 'L02', itemCode: 'RACK-BEAM-2.7M', itemName: 'Racking Beam 2.7m', unit: 'PCS', plannedQty: 180, issuedQty: 178, returnedQty: 2 },
      { id: 'L03', itemCode: 'ANCHOR-BOLT-M12', itemName: 'Anchor Bolt M12', unit: 'PCS', plannedQty: 240, issuedQty: 235, returnedQty: 5 },
    ],
  },
  {
    id: 'BOM-003',
    projectName: 'Electrical Substation Upgrade',
    projectCode: 'ELEC-SUB-UPG',
    site: 'Site Beta',
    projectManager: 'Rahul Kumar',
    startDate: '01 Jun 2026',
    endDate: '31 Dec 2026',
    status: 'ACTIVE',
    totalBOMValue: 415000,
    lines: [
      { id: 'L01', itemCode: 'CABLE-XLPE-95MM', itemName: 'XLPE Cable 95mm² (100m)', unit: 'ROLL', plannedQty: 40, issuedQty: 12, returnedQty: 0 },
      { id: 'L02', itemCode: 'CONDUIT-GI-50MM', itemName: 'GI Conduit 50mm (3m)', unit: 'PCS', plannedQty: 300, issuedQty: 85, returnedQty: 0 },
      { id: 'L03', itemCode: 'CB-3P-400A', itemName: 'Circuit Breaker 3P 400A', unit: 'PCS', plannedQty: 8, issuedQty: 3, returnedQty: 0 },
      { id: 'L04', itemCode: 'LUGS-95MM-CRIMP', itemName: 'Crimp Lugs 95mm²', unit: 'PCS', plannedQty: 160, issuedQty: 48, returnedQty: 0 },
    ],
  },
  {
    id: 'BOM-004',
    projectName: 'Cooling Tower Maintenance',
    projectCode: 'CT-MAINT-Q3',
    site: 'Site Gamma',
    projectManager: 'Arjun Mehta',
    startDate: '01 Jul 2026',
    endDate: '31 Aug 2026',
    status: 'ACTIVE',
    totalBOMValue: 38750,
    lines: [
      { id: 'L01', itemCode: 'FILL-MEDIA-PVC', itemName: 'PVC Fill Media (m²)', unit: 'M2', plannedQty: 200, issuedQty: 182, returnedQty: 0 },
      { id: 'L02', itemCode: 'PUMP-SEAL-KIT', itemName: 'Pump Mechanical Seal Kit', unit: 'SET', plannedQty: 4, issuedQty: 4, returnedQty: 0 },
      { id: 'L03', itemCode: 'BEARING-6205-ZZ', itemName: 'Ball Bearing 6205-ZZ', unit: 'PCS', plannedQty: 8, issuedQty: 9, returnedQty: 0 },
      { id: 'L04', itemCode: 'ANTI-SCALE-25L', itemName: 'Anti-Scale Chemical 25L', unit: 'CAN', plannedQty: 10, issuedQty: 7, returnedQty: 1 },
    ],
  },
  {
    id: 'BOM-005',
    projectName: 'Site Perimeter Fencing',
    projectCode: 'FENCE-ALPHA-1',
    site: 'Site Alpha',
    projectManager: 'Priya Sharma',
    startDate: '10 Jan 2026',
    endDate: '28 Feb 2026',
    status: 'COMPLETED',
    totalBOMValue: 29100,
    lines: [
      { id: 'L01', itemCode: 'CHAIN-LINK-2M', itemName: 'Chain Link Fencing 2m (25m roll)', unit: 'ROLL', plannedQty: 20, issuedQty: 20, returnedQty: 0 },
      { id: 'L02', itemCode: 'POST-GI-50X50', itemName: 'GI Post 50×50mm (3m)', unit: 'PCS', plannedQty: 80, issuedQty: 78, returnedQty: 0 },
    ],
  },
  {
    id: 'BOM-006',
    projectName: 'HVAC Duct Replacement',
    projectCode: 'HVAC-DUCT-WH',
    site: 'Warehouse B',
    projectManager: 'Rahul Kumar',
    startDate: '15 Aug 2026',
    endDate: '30 Nov 2026',
    status: 'DRAFT',
    totalBOMValue: 91200,
    lines: [
      { id: 'L01', itemCode: 'DUCT-GI-300X200', itemName: 'GI Duct 300×200mm (1.2m)', unit: 'PCS', plannedQty: 150, issuedQty: 0, returnedQty: 0 },
      { id: 'L02', itemCode: 'DUCT-FLEX-200MM', itemName: 'Flexible Duct 200mm (3m)', unit: 'PCS', plannedQty: 60, issuedQty: 0, returnedQty: 0 },
      { id: 'L03', itemCode: 'DAMPER-FIRE-300', itemName: 'Fire Damper 300mm', unit: 'PCS', plannedQty: 12, issuedQty: 0, returnedQty: 0 },
    ],
  },
  {
    id: 'BOM-007',
    projectName: 'Pipeline Corrosion Repair',
    projectCode: 'PIPE-CORR-B2',
    site: 'Site Beta',
    projectManager: 'Arjun Mehta',
    startDate: '01 Feb 2026',
    endDate: '15 Mar 2026',
    status: 'CANCELLED',
    totalBOMValue: 17400,
    lines: [
      { id: 'L01', itemCode: 'EPOXY-COAT-20L', itemName: 'Epoxy Coating 20L', unit: 'CAN', plannedQty: 30, issuedQty: 5, returnedQty: 5 },
      { id: 'L02', itemCode: 'SAND-BLAST-GRIT', itemName: 'Sandblasting Grit (25kg)', unit: 'BAG', plannedQty: 100, issuedQty: 10, returnedQty: 10 },
    ],
  },
];

const STATUS_TABS: Array<{ key: ProjectStatus | 'ALL'; label: string; color: string }> = [
  { key: 'ALL', label: 'All Projects', color: 'text-gray-600' },
  { key: 'DRAFT', label: 'Draft', color: 'text-gray-500' },
  { key: 'ACTIVE', label: 'Active', color: 'text-blue-600' },
  { key: 'COMPLETED', label: 'Completed', color: 'text-teal-600' },
  { key: 'CANCELLED', label: 'Cancelled', color: 'text-red-500' },
];

const STATUS_BADGE: Record<ProjectStatus, { bg: string; text: string; label: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Draft' },
  ACTIVE: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Active' },
  COMPLETED: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Completed' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-600', label: 'Cancelled' },
};

type TrafficLight = 'GREEN' | 'AMBER' | 'RED' | 'PURPLE';

function getTrafficLight(line: BOMLine): TrafficLight {
  const net = line.issuedQty - line.returnedQty;
  if (line.plannedQty === 0) return 'GREEN';
  if (net > line.plannedQty) return 'PURPLE';
  const pct = (net / line.plannedQty) * 100;
  if (pct >= 90) return 'RED';
  if (pct >= 75) return 'AMBER';
  return 'GREEN';
}

const TRAFFIC_CONFIG: Record<TrafficLight, { bar: string; badge: string; badgeText: string; label: string; icon: string }> = {
  GREEN: { bar: '#1E7F74', badge: 'bg-teal-100', badgeText: 'text-teal-700', label: '< 75%', icon: '✓' },
  AMBER: { bar: '#E8A838', badge: 'bg-amber-100', badgeText: 'text-amber-700', label: '75–90%', icon: '!' },
  RED: { bar: '#E24B4A', badge: 'bg-red-100', badgeText: 'text-red-700', label: '> 90%', icon: '!!' },
  PURPLE: { bar: '#7C3AED', badge: 'bg-purple-100', badgeText: 'text-purple-700', label: 'Over', icon: '▲' },
};

function getProjectTrafficSummary(lines: BOMLine[]) {
  const counts = { GREEN: 0, AMBER: 0, RED: 0, PURPLE: 0 };
  lines.forEach((l) => { counts[getTrafficLight(l)]++; });
  return counts;
}

function getProjectOverallPct(lines: BOMLine[]): number {
  const totalPlanned = lines.reduce((s, l) => s + l.plannedQty, 0);
  const totalNet = lines.reduce((s, l) => s + Math.max(0, l.issuedQty - l.returnedQty), 0);
  if (totalPlanned === 0) return 0;
  return Math.round((totalNet / totalPlanned) * 100);
}

interface LineProgressBarProps {
  line: BOMLine;
}

function LineProgressBar({ line }: LineProgressBarProps) {
  const net = line.issuedQty - line.returnedQty;
  const tl = getTrafficLight(line);
  const cfg = TRAFFIC_CONFIG[tl];
  const issuedPct = line.plannedQty > 0 ? Math.min((line.issuedQty / line.plannedQty) * 100, 120) : 0;
  const returnedPct = line.plannedQty > 0 ? Math.min((line.returnedQty / line.plannedQty) * 100, 20) : 0;
  const netPct = line.plannedQty > 0 ? Math.min((net / line.plannedQty) * 100, 120) : 0;
  const overConsumed = net > line.plannedQty;

  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
      {/* Traffic light dot */}
      <div
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ background: cfg.bar }}
        title={cfg.label}
      />
      {/* Item info */}
      <div className="w-36 shrink-0">
        <p className="text-[11px] font-600 text-gray-800 truncate" title={line.itemName}>{line.itemName}</p>
        <p className="text-[10px] text-gray-400">{line.itemCode}</p>
      </div>
      {/* Progress bars */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Planned baseline */}
        <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
          {/* Issued bar */}
          <div
            className="absolute left-0 top-0 h-full rounded-full opacity-40 transition-all duration-500"
            style={{ width: `${Math.min(issuedPct, 100)}%`, background: cfg.bar }}
          />
          {/* Net consumed bar */}
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(netPct, 100)}%`, background: cfg.bar }}
          />
          {/* Over-consumed overflow indicator */}
          {overConsumed && (
            <div
              className="absolute right-0 top-0 h-full rounded-r-full"
              style={{ width: '8px', background: '#7C3AED' }}
            />
          )}
          {/* Planned marker line */}
          <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-gray-400/60" />
        </div>
        {/* Legend row */}
        <div className="flex items-center gap-3 text-[9px] text-gray-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-1.5 rounded-sm inline-block" style={{ background: cfg.bar, opacity: 0.4 }} />
            Issued: <span className="font-600 text-gray-600">{line.issuedQty} {line.unit}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-1.5 rounded-sm inline-block bg-gray-300" />
            Returned: <span className="font-600 text-gray-600">{line.returnedQty} {line.unit}</span>
          </span>
          <span className="flex items-center gap-1">
            Net: <span className="font-600 text-gray-700">{net} / {line.plannedQty} {line.unit}</span>
          </span>
        </div>
      </div>
      {/* Traffic light badge */}
      <div className={`shrink-0 px-2 py-0.5 rounded-full text-[9px] font-700 ${cfg.badge} ${cfg.badgeText}`}>
        {overConsumed ? 'OVER' : `${Math.round(netPct)}%`}
      </div>
    </div>
  );
}

interface ProjectCardProps {
  project: BOMProject;
  expanded: boolean;
  onToggle: () => void;
}

function ProjectCard({ project, expanded, onToggle }: ProjectCardProps) {
  const statusCfg = STATUS_BADGE[project.status];
  const summary = getProjectTrafficSummary(project.lines);
  const overallPct = getProjectOverallPct(project.lines);
  const hasAlert = summary.RED > 0 || summary.PURPLE > 0;

  // Overall project bar color
  let overallBarColor = '#1E7F74';
  if (summary.PURPLE > 0) overallBarColor = '#7C3AED';
  else if (summary.RED > 0) overallBarColor = '#E24B4A';
  else if (summary.AMBER > 0) overallBarColor = '#E8A838';

  return (
    <div className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all duration-200 ${hasAlert ? 'border-red-200' : 'border-gray-200'}`}>
      {/* Card header */}
      <div
        className="flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50/60 transition-colors"
        onClick={onToggle}
      >
        {/* Left: project info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-1">
            <span className="text-[13px] font-700 text-[#1A3A5C] truncate">{project.projectName}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-700 ${statusCfg.bg} ${statusCfg.text}`}>
              {statusCfg.label}
            </span>
            {hasAlert && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-[10px] font-700 text-red-600">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                Alert
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap text-[11px] text-gray-500">
            <span className="flex items-center gap-1">
              <Icon name="TagIcon" size={11} className="text-gray-400" />
              <span className="font-600 text-gray-700">{project.projectCode}</span>
            </span>
            <span className="flex items-center gap-1">
              <Icon name="MapPinIcon" size={11} className="text-gray-400" />
              {project.site}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="UserIcon" size={11} className="text-gray-400" />
              {project.projectManager}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="CalendarDaysIcon" size={11} className="text-gray-400" />
              {project.startDate} → {project.endDate}
            </span>
          </div>
        </div>

        {/* Right: summary stats */}
        <div className="flex items-center gap-4 shrink-0">
          {/* Traffic light dots summary */}
          <div className="hidden sm:flex items-center gap-1.5">
            {(['GREEN', 'AMBER', 'RED', 'PURPLE'] as TrafficLight[]).map((tl) => {
              const count = summary[tl];
              if (count === 0) return null;
              return (
                <div key={tl} className="flex items-center gap-1" title={`${TRAFFIC_CONFIG[tl].label}: ${count} line(s)`}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: TRAFFIC_CONFIG[tl].bar }} />
                  <span className="text-[10px] font-600 text-gray-600">{count}</span>
                </div>
              );
            })}
          </div>

          {/* Overall % */}
          <div className="text-right">
            <p className="text-[11px] text-gray-400">Consumed</p>
            <p className="text-[18px] font-700 tabular-nums" style={{ color: overallBarColor }}>{overallPct}%</p>
          </div>

          {/* BOM value */}
          <div className="text-right hidden md:block">
            <p className="text-[11px] text-gray-400">BOM Value</p>
            <p className="text-[13px] font-700 text-gray-700">€{project.totalBOMValue.toLocaleString()}</p>
          </div>

          {/* Expand chevron */}
          <div className={`w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
            <Icon name="ChevronDownIcon" size={14} className="text-gray-500" />
          </div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="px-5 pb-3">
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(overallPct, 100)}%`, background: overallBarColor }}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[9px] text-gray-400">{project.lines.length} BOM lines</span>
          <span className="text-[9px] text-gray-400">{overallPct}% of planned consumed</span>
        </div>
      </div>

      {/* Expanded: per-line consumption */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 py-3 bg-gray-50/40">
          {/* Legend */}
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <span className="text-[10px] font-700 text-gray-500 uppercase tracking-wide">Per-Line Consumption</span>
            <div className="flex items-center gap-3 ml-auto">
              {(['GREEN', 'AMBER', 'RED', 'PURPLE'] as TrafficLight[]).map((tl) => (
                <div key={tl} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: TRAFFIC_CONFIG[tl].bar }} />
                  <span className="text-[9px] text-gray-500">{TRAFFIC_CONFIG[tl].label}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Lines */}
          <div>
            {project.lines.map((line) => (
              <LineProgressBar key={line.id} line={line} />
            ))}
          </div>
          {/* Footer actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A3A5C] text-white text-[11px] font-600 hover:bg-[#2E6DA4] transition-colors">
              <Icon name="DocumentChartBarIcon" size={13} />
              Consumption Report
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-[11px] font-600 hover:bg-gray-100 transition-colors">
              <Icon name="EyeIcon" size={13} />
              View Full BOM
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BOMProjectsList() {
  const [activeTab, setActiveTab] = useState<ProjectStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['BOM-001']));

  const filtered = PROJECTS.filter((p) => {
    const matchTab = activeTab === 'ALL' || p.status === activeTab;
    const q = search.toLowerCase();
    const matchSearch = !q || p.projectName.toLowerCase().includes(q) || p.projectCode.toLowerCase().includes(q) || p.site.toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const tabCounts = STATUS_TABS.reduce<Record<string, number>>((acc, tab) => {
    acc[tab.key] = tab.key === 'ALL' ? PROJECTS.length : PROJECTS.filter((p) => p.status === tab.key).length;
    return acc;
  }, {});

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Summary KPIs
  const activeProjects = PROJECTS.filter((p) => p.status === 'ACTIVE');
  const alertProjects = activeProjects.filter((p) => {
    const s = getProjectTrafficSummary(p.lines);
    return s.RED > 0 || s.PURPLE > 0;
  });
  const totalValue = PROJECTS.filter((p) => p.status === 'ACTIVE').reduce((s, p) => s + p.totalBOMValue, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#1A3A5C] to-[#2E6DA4] rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <Icon name="ClipboardDocumentListIcon" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-700 text-base">BOM Controller</p>
          <p className="text-[12px] text-white/65">Project-based material consumption tracking · Traffic-light status per line</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E8A838] text-[#1A3A5C] text-[12px] font-700 hover:bg-[#d4962e] transition-colors shrink-0">
          <Icon name="PlusIcon" size={15} />
          New Project
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Active Projects', value: activeProjects.length, icon: 'PlayCircleIcon', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
          { label: 'Consumption Alerts', value: alertProjects.length, icon: 'ExclamationTriangleIcon', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', pulse: alertProjects.length > 0 },
          { label: 'Active BOM Value', value: `€${(totalValue / 1000).toFixed(0)}k`, icon: 'BanknotesIcon', color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' },
          { label: 'Total Projects', value: PROJECTS.length, icon: 'FolderIcon', color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
        ].map((kpi) => (
          <div key={kpi.label} className={`rounded-2xl border ${kpi.bg} ${kpi.border} px-4 py-3.5 flex items-center gap-3 shadow-sm`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${kpi.bg}`}>
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={18} className={kpi.color} />
            </div>
            <div>
              <p className="text-[10px] font-700 uppercase tracking-widest text-gray-500">{kpi.label}</p>
              <div className="flex items-center gap-1.5">
                <p className={`text-2xl font-700 tabular-nums ${kpi.color}`}>{kpi.value}</p>
                {kpi.pulse && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Traffic light legend */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2 flex-wrap shadow-sm">
        <span className="text-[10px] font-700 text-gray-500 uppercase tracking-wide mr-2">Consumption Status:</span>
        {(['GREEN', 'AMBER', 'RED', 'PURPLE'] as TrafficLight[]).map((tl) => (
          <div key={tl} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${TRAFFIC_CONFIG[tl].badge}`}>
            <div className="w-2 h-2 rounded-full" style={{ background: TRAFFIC_CONFIG[tl].bar }} />
            <span className={`text-[10px] font-600 ${TRAFFIC_CONFIG[tl].badgeText}`}>
              {tl === 'GREEN' && '< 75% — On Track'}
              {tl === 'AMBER' && '75–90% — Watch'}
              {tl === 'RED' && '> 90% — Critical'}
              {tl === 'PURPLE' && 'Over-Consumed'}
            </span>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status tabs */}
        <div className="flex items-center bg-white border border-gray-200 rounded-xl p-1 gap-0.5 shadow-sm flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={[
                'px-3 py-1.5 rounded-lg text-[12px] font-600 transition-all duration-150 flex items-center gap-1.5',
                activeTab === tab.key
                  ? 'bg-[#1A3A5C] text-white shadow-sm'
                  : `${tab.color} hover:bg-gray-100`,
              ].join(' ')}
            >
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-700 ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {tabCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm flex-1 min-w-[200px] max-w-xs">
          <Icon name="MagnifyingGlassIcon" size={14} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Search project, code, site…"
            value={search}
            onChange={(e) => setSearch(e?.target?.value ?? '')}
            className="flex-1 text-[12px] text-gray-700 placeholder-gray-400 bg-transparent outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              <Icon name="XMarkIcon" size={13} />
            </button>
          )}
        </div>

        <div className="ml-auto text-[11px] text-gray-400">
          {filtered.length} project{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Project cards */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-12 text-center shadow-sm">
            <Icon name="FolderOpenIcon" size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-[14px] font-600 text-gray-500">No projects found</p>
            <p className="text-[12px] text-gray-400 mt-1">Try adjusting your filters or search term</p>
          </div>
        ) : (
          filtered.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              expanded={expandedIds.has(project.id)}
              onToggle={() => toggleExpand(project.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
